import * as React from "react";

import { AjaxManager } from "@impero-com/common/utils/ajax";
import { getParentElement } from "@impero-com/common/utils/dom";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";
import { InfoTooltip } from "@impero-com/ui";
import { Radio, RadioChangeEvent } from "antd";

import type { UserProfile } from "api/generated/model";
import {
    UserGroupViewDto as UserGroupWithDetails,
    InitialAssignmentKind,
    UserGroupView,
    AvailableActions,
    UserOverview,
} from "api/generated/model";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { UserOrUserGroupTypeahead } from "components/userTypeahead";
import { UserOrUserGroupOverview } from "utils/user";

import { ControlAssignmentResources } from "../types";

import { DefaultClaimantSelectorAsync } from "./defaultClaimant";
import * as theme from "./theme.css";
import { toUserOrUserGroupOverviewEnum } from "./types";

class ResponsibleSection extends React.PureComponent<
    ResponsibleSectionProps,
    ResponsibleSectionState
> {
    state: ResponsibleSectionState = {
        userGroup: undefined,
    };
    render = () => {
        const { value } = this.props;
        const {
            resources,
            ajaxManager,
            availableActions,
            orgCookie,
            orgProfileCookie,
            defaultClaimantError,
        } = this.props;
        let responsible: UserOrUserGroupOverview | undefined;
        let assignmentTypeRadio: React.ReactNode | undefined;
        let claimantWidget: React.ReactNode | undefined;
        switch (value.type) {
            case "User":
                responsible = resources.users.find(
                    (userDto) => userDto.resource.id === value.userId
                )!.resource;
                assignmentTypeRadio = undefined;
                break;
            default:
                responsible = resources.userGroups.find(
                    (groupDto) => groupDto.resource.id === value.userGroupId
                )!.resource;
                switch (value.type) {
                    case "Pool":
                        assignmentTypeRadio = this.buildAssignmentTypeRadio(
                            "Pool",
                            responsible.userGroupUsers.length
                        );
                        claimantWidget = (
                            <DefaultClaimantSelectorAsync
                                {...{ ajaxManager }}
                                userGroupId={value.userGroupId}
                                defaultClaimantId={
                                    value.defaultClaimantId !== null
                                        ? value.defaultClaimantId
                                        : undefined
                                }
                                onChange={this.handleDefaultClaimantChange}
                                error={defaultClaimantError}
                            />
                        );
                        break;
                    case "Group":
                        assignmentTypeRadio = this.buildAssignmentTypeRadio(
                            "Group",
                            responsible.userGroupUsers.length
                        );
                        claimantWidget = undefined;
                        break;
                    default:
                        return assertNever(value);
                }
        }
        return (
            <>
                <BaseFormDisplay label={t("ControlResult_Assignee")}>
                    <div>
                        <UserOrUserGroupTypeahead
                            id="responsible"
                            value={responsible}
                            onSelect={this.handleResponsibleChanged}
                            {...{
                                ajaxManager,
                                availableActions,
                                orgCookie,
                                orgProfileCookie,
                            }}
                            getPopupContainer={getParentElement}
                        />
                    </div>
                </BaseFormDisplay>
                {assignmentTypeRadio}
                {claimantWidget}
            </>
        );
    };

    private buildAssignmentTypeRadio = (
        assignmentType: GroupAssignmentType,
        userCount: number
    ) => {
        return (
            <div className={theme.assignmentTypesContainer}>
                <BaseFormDisplay
                    label={t("CreateControl_Assignees_AssignmentType")}
                >
                    <Radio.Group
                        value={assignmentType}
                        onChange={this.handleGroupAssignmentTypeRadioChanged}
                    >
                        <AssignmentTypeRadio
                            key="pool"
                            assignmentType="Pool"
                            label={t("CreateControl_Assignee_GroupAssignment")}
                            tooltipText={t(
                                "CreateControl_Assignee_GroupAssignment_Tooltip"
                            )}
                            userCount={1}
                            currentAssignmentType={assignmentType}
                        />
                        <AssignmentTypeRadio
                            key="group"
                            assignmentType="Group"
                            label={t(
                                "CreateControl_Assignee_IndividualAssignment"
                            )}
                            tooltipText={t(
                                "CreateControl_Assignee_IndividualAssignment_Tooltip"
                            )}
                            {...{ userCount }}
                            currentAssignmentType={assignmentType}
                        />
                    </Radio.Group>
                </BaseFormDisplay>
            </div>
        );
    };

    private handleResponsibleChanged = (
        rawResponsible: UserOrUserGroupOverview
    ) => {
        const { value, onUserAdded, onUserGroupAdded } = this.props;
        const oldAssigneeKind = value;
        let newAssigneeKind: InitialAssignmentKind;
        const responsible = toUserOrUserGroupOverviewEnum(rawResponsible);
        switch (responsible.type) {
            case "User":
                onUserAdded(responsible.user);
                newAssigneeKind = {
                    type: "User",
                    userId: responsible.user.id,
                };
                break;
            case "UserGroup":
                onUserGroupAdded(responsible.userGroup);
                switch (oldAssigneeKind.type) {
                    case "User":
                    case "Group":
                        newAssigneeKind = {
                            type: "Group",
                            userGroupId: responsible.userGroup.id,
                        };
                        break;
                    case "Pool":
                        newAssigneeKind = {
                            type: "Pool",
                            userGroupId: responsible.userGroup.id,
                            defaultClaimantId: null,
                        };
                        break;
                    default:
                        assertNever(oldAssigneeKind);
                }
                break;
            default:
                assertNever(responsible);
        }
        this.onResponsibleChanged(newAssigneeKind);
    };

    private handleGroupAssignmentTypeRadioChanged = (e: RadioChangeEvent) => {
        const oldAssigneeKind = this.props.value;
        if (oldAssigneeKind.type === "User") {
            throw new Error(
                "The assignment type radio should not be visible when the assignee kind is 'User'"
            );
        }
        const isGroup = e.target.value === "Group";
        let newAssigneeKind: InitialAssignmentKind;
        switch (oldAssigneeKind.type) {
            case "Group":
                if (isGroup) {
                    return;
                }
                newAssigneeKind = {
                    type: "Pool",
                    userGroupId: oldAssigneeKind.userGroupId,
                    defaultClaimantId: null,
                };
                break;
            case "Pool":
                if (!isGroup) {
                    return;
                }
                newAssigneeKind = {
                    type: "Group",
                    userGroupId: oldAssigneeKind.userGroupId,
                };
                break;
            default:
                assertNever(oldAssigneeKind);
        }
        this.onResponsibleChanged(newAssigneeKind);
    };

    private handleDefaultClaimantChange = (
        defaultClaimant: UserOverview | undefined
    ) => {
        const { value: assigneeKind, onUserAdded } = this.props;
        if (assigneeKind.type !== "Pool") {
            throw new Error(
                "Trying to set default claimant but the type of the assignee is " +
                    assigneeKind.type
            );
        }
        const defaultClaimantId =
            defaultClaimant !== undefined ? defaultClaimant.id : null;
        if (assigneeKind.defaultClaimantId === defaultClaimantId) {
            return;
        }
        const newAssigneeKind = {
            ...assigneeKind,
            defaultClaimantId,
        };
        if (defaultClaimant) {
            onUserAdded(defaultClaimant);
        }
        this.onResponsibleChanged(newAssigneeKind);
    };

    private onResponsibleChanged = (assigneeKind: InitialAssignmentKind) => {
        if (assigneeKind.type !== "Pool" && this.state.userGroup) {
            this.setState({ userGroup: undefined });
        }
        this.props.onResponsibleChanged(assigneeKind);
    };
}

interface ResponsibleSectionProps {
    value: InitialAssignmentKind;
    resources: ControlAssignmentResources;
    ajaxManager: AjaxManager;
    availableActions: AvailableActions;
    orgCookie: OrgCookie;
    orgProfileCookie: UserProfile;
    onUserAdded: (user: UserOverview) => void;
    onUserGroupAdded: (userGroup: UserGroupView) => void;
    onResponsibleChanged: (assigneeKind: InitialAssignmentKind) => void;
    defaultClaimantError?: string;
}

interface ResponsibleSectionState {
    userGroup: UserGroupWithDetails | undefined;
}

type GroupAssignmentType = "Group" | "Pool";

class AssignmentTypeRadio extends React.PureComponent<AssignmentTypeRadioProps> {
    render = () => {
        const {
            assignmentType,
            label,
            tooltipText,
            userCount,
            currentAssignmentType,
        } = this.props;
        return (
            <div className={theme.assignmentTypeContainer}>
                <div>
                    <Radio
                        key={assignmentType}
                        value={assignmentType}
                        checked={currentAssignmentType === assignmentType}
                    >
                        {label}
                    </Radio>
                    <InfoTooltip text={tooltipText} size="small" />
                </div>
                <div className={theme.notificationCount}>
                    {t("CreateControl_Assignee_Control", { count: userCount })}
                </div>
            </div>
        );
    };
}

interface AssignmentTypeRadioProps {
    assignmentType: InitialAssignmentKind["type"];
    label: string;
    tooltipText: string;
    userCount: number;
    currentAssignmentType: InitialAssignmentKind["type"];
}

export { ResponsibleSection };
